import { Component, Inject, Input, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FeatureTableData } from '../../../core/interfaces/feature-table';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'mivb-feature-table',
  templateUrl: './feature-table.component.html',
  styleUrls: ['./feature-table.component.scss']
})
export class FeatureTableComponent implements OnInit {

  title: string;
  featureClicked: number;
  tableLength: number;
  currentSelectedFeature: string;
  activeIndex: number;
  table: FeatureTableData;

  /*

    https://mitel.atlassian.net/browse/CLSD-885

    the table is in the form of an object:
    data = {
      title: string,
      value: string,
      headers: { name: string, active: boolean, value: string, hide?: boolean, disabled?: boolean }[],
      featureRows?: { name: string, items: any[] }[],
      rowGroups?: {
          groupName: string,
          featureRows: { name: string, items: any[]}[];
      }
    }

    Headers.name will show the in the table header. It should be in the form of a translate string (mivb_config.header.title)
    this is also true of all other name properties, including rowGroups.groupName and title.
    
    Value string will be used to send what header was selected upon save.

    Hide boolean will be used to determine whether the column will be displayed or not

    Disabled boolean will be used to determine if the column is selectable or not
    This property is only applicable for not hidden columns
    Also used to determine which icon to show for boolean items with true value

    The featuresRows name will be shown in the y axis of the table. The items array holds the item for each header. The items should be 
    in order of how they appear in the table. translate strings for words, and for boolean, true to show a check mark and false to show none.

    rowGroups allow for featureRows to appear in vertically grouped sections. The groupName will be displayed on the side. In that object there
    are features that will be apart of that group.

    A table will have either just featureRows, OR have row groups.

    examples:

    const table_with_just_feature_rows = {
      title: 'mivb_config.feature_table.mitel_one.title',
      value: 'mitel_one_table',
      headers: [
        { name: 'mivb_config.feature_table.mitel_one.column_name.mivb_telephony', active: false, value: 'mivb_telephony', hide: true },
        { name: 'mivb_config.feature_table.mitel_one.column_name.mivb_entry', active: false, value: 'mivb_entry', hide: false, disabled: true },
        { name: 'mivb_config.feature_table.mitel_one.column_name.mivb_elite', active: false, value: 'mivb_elite', hide: false, disabled: false },
        { name: 'mivb_config.feature_table.mitel_one.column_name.mivb_premier', active: false, value: 'mivb_premier' }
        ...
      ],
      featureRows: [
         {
            name: 'mivb_config.feature_table.mivbusiness.row_name.single_number',
            items: [
                true, (boolean will show up as blue checkmark if header is not disabled)
                true, (boolean will show up as grey checkmark if header is disabled)
                ...
            ]
        },
        {
            name: 'mivb_config.feature_table.mivbusiness.row_name.voice_messaging_email',
            items: [
                'mivb_config.feature_table.mivbusiness.rowEntry.emem', (string will show up as translated string)
                'mivb_config.feature_table.mivbusiness.rowEntry.emem',
                ...
            ]
        },
        ...
        
      ]
    }

    const table_with_row_groups = {
      title: 'mivb_config.feature_table.mitel_one.title',
      value: 'mitel_one_table',
      headers: [
       { name: 'mivb_config.feature_table.mitel_one.column_name.mivb_telephony', active: false, value: 'mivb_telephony', hide: true },
        { name: 'mivb_config.feature_table.mitel_one.column_name.mivb_entry', active: false, value: 'mivb_entry', hide: false, disabled: true },
        { name: 'mivb_config.feature_table.mitel_one.column_name.mivb_elite', active: false, value: 'mivb_elite', hide: false, disabled: false },
        { name: 'mivb_config.feature_table.mitel_one.column_name.mivb_premier', active: false, value: 'mivb_premier' }
        ...
      ],
      rowGroups: [
        {
          groupName: 'mivb_config.feature_table.mitel_one.mivbusiness.row_group_name',
          featureRows: [
            {
              name: 'mivb_config.feature_table.mitel_one.mivbusiness.row_name.all_mivb_features',
              items: [
                true, (boolean will show up as blue checkmark if header is not disabled)
                true, (boolean will show up as grey checkmark if header is disabled)
                true,
                false (empty cell)
              ]
            },
            {
              name: 'mivb_config.feature_table.mitel_one.mivbusiness.row_name.single_number',
              items: [
                true,
                true,
                false,
                false
              ]
            },
            ...
          ]
        }
        {
          groupName: 'mivb_config.feature_table.mitel_one.micollab.row_group_name',
          featureRows: [
            {
              name: 'mivb_config.feature_table.mitel_one.micollab.row_name.unified_messaging',
              items: [
                false, 
                true, 
                false, 
                true
              ]
            },
            ...
          ]
        }
      ]
    */

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<FeatureTableComponent>,
    public translateSvc: TranslateService
  ) {
    this.table = data;
    this.tableLength = this.calculateNumberOfRows();
  }

  ngOnInit(): void {
    console.log('feature table data', this.table);
  }
  
  selectColumn(index: number) {
    this.table.headers[index].active = !this.table.headers[index].active;
    this.featureClicked = index;
    this.activeIndex = index;
    this.currentSelectedFeature = this.table.headers[index].value;
    this.table.headers[index].active = true;
  }

  onClose(data?: string) {
    setTimeout(() => {
      this.dialogRef.close(data);
      this.currentSelectedFeature = '';
      this.featureClicked = -1;
      this.activeIndex = -1;
    }, 200);
  }

  onSave() {
    this.onClose(this.currentSelectedFeature);
  }

  isBoolean(val: any) {
    return typeof val === 'boolean';
  }

  isString(val: any) {
    return typeof val === 'string';
  }

  isNumber(val: any) {
    return typeof val === 'number';
  }

  calculateNumberOfRows() {
    if (this.table.featureRows) {
      return this.table.featureRows.length;
    } else {
      let count = 0;
      for (const group of this.table.rowGroups) {
        count += group.featureRows.length;
      }
      return count;
    }
  }
}